import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Calendar,
  Clock,
  Disc,
  Edit2,
  Heart,
  ListMusic,
  Loader,
  MoreHorizontal,
  Play,
  Plus,
  Share2,
  Shuffle,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AddTracksDialog } from "../components/AddTracksDialog";
import { AlbumManager } from "../components/AlbumManager";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { TrackList } from "../components/TrackList";
import { useSpotifyAuth } from "../hooks/useSpotifyAuth";
import { spotifyApi } from "../lib/spotify";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { usePlayerStore } from "../stores/playerStore";
import { Album, Track } from "../types";
import { formatTime } from "../utils/formatTime";

export const AlbumDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { albums, deleteAlbum } = useLocalLibraryStore();

  // Only find from albums, not playlists
  const localAlbum = albums.find((album) => album.id === id);

  const { isAuthenticated } = useSpotifyAuth();
  const { getAllAlbums, addTrackToAlbum, removeTrackFromAlbum, isContentLiked, toggleLike } = useLocalLibraryStore();
  const { setCurrentTrack, setIsPlaying, setQueue } = usePlayerStore();
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddTracks, setShowAddTracks] = useState(false);

  // If not local, fetch from Spotify
  const { data: spotifyAlbum, isLoading } = useQuery({
    queryKey: ["album", id],
    queryFn: () => spotifyApi.albums.get(id!),
    enabled: !localAlbum && isAuthenticated,
  });

  // Function to fetch demo album
  const { data: demoAlbum } = useQuery({
    queryKey: ["demo-album", id],
    queryFn: async () => {
      const response = await fetch("/demo/tracks.json");
      const data = await response.json();
      const foundAlbum = data.albums.find((album: Album) => album.id === id);
      if (foundAlbum) {
        // Ensure album tracks have all required properties
        foundAlbum.tracks = foundAlbum.tracks.map((track: Track) => ({
          ...track,
          source: "local",
          album: foundAlbum.name,
          albumId: foundAlbum.id,
        }));
      }
      return foundAlbum;
    },
    enabled: !localAlbum, // Allow demo album query even when not authenticated
  });

  // Update album selection to include demo album
  const album: Album | any | undefined = localAlbum || spotifyAlbum || demoAlbum;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
          <Loader className="w-8 h-8 animate-spin text-yellow-500" />
          <p className="text-zinc-400">Loading album...</p>
        </motion.div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold mb-4">Album not found</h1>
        <button onClick={() => navigate(-1)} className="text-yellow-500 hover:underline">
          Go back
        </button>
      </div>
    );
  }

  const tracks: Track[] = localAlbum
    ? album.tracks
    : spotifyAlbum?.tracks.items.map((track) => ({
        id: track.id,
        title: track.name,
        artist: track.artists[0].name,
        albumArt: spotifyAlbum.images[0]?.url,
        duration: track.duration_ms / 1000,
        url: track.preview_url || "",
        source: "spotify" as const,
        spotifyData: track,
      })) || [];

  const totalDuration = tracks.reduce((acc, track) => acc + track.duration, 0);

  const handlePlay = () => {
    if (tracks.length > 0) {
      setCurrentTrack(tracks[0]);
      setQueue(tracks);
      setIsPlaying(true);
    }
  };

  const handleShuffle = () => {
    if (tracks.length > 0) {
      const shuffledTracks = [...tracks].sort(() => Math.random() - 0.5);
      setCurrentTrack(shuffledTracks[0]);
      setQueue(shuffledTracks);
      setIsPlaying(true);
    }
  };

  const handleDelete = () => {
    if (localAlbum) {
      deleteAlbum(album.id);
      navigate("/albums");
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: album.name,
        text: `Check out this album: ${album.name}`,
        url: window.location.href,
      });
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const handleAddTracks = (newTracks: Track[]) => {
    if (localAlbum) {
      newTracks.forEach((track) => {
        addTrackToAlbum(album.id, track);
      });
    }
    setShowAddTracks(false);
  };

  const handleRemoveTrack = (trackId: string) => {
    if (localAlbum) {
      removeTrackFromAlbum(album.id, trackId);
    }
  };

  const isLiked = isContentLiked(album.id, "album");
  const source = localAlbum ? ("local" as const) : ("spotify" as const);

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-800/50 to-zinc-900">
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
        {/* Album Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
          <div className="space-y-4">
            <h1 className="text-3xl md:text-4xl font-bold">{album.name}</h1>
            {album.description && <p className="text-zinc-400">{album.description}</p>}
            <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
              <div className="flex items-center gap-2">
                <Disc className="w-4 h-4" />
                <span>
                  {localAlbum
                    ? album.tracks[0]?.artist || album.artist
                    : spotifyAlbum?.artists[0].name || "Unknown Artist"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <ListMusic className="w-4 h-4" />
                <span>{tracks.length} tracks</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{formatTime(totalDuration)}</span>
              </div>
              {localAlbum && "createdAt" in album && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(album.createdAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handlePlay}
              className="flex-1 md:flex-none px-8 py-3 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              Play
            </button>
            <div className="flex items-center">
              <button onClick={handleShuffle} className="p-3 text-zinc-400 hover:text-white transition">
                <Shuffle className="w-5 h-5" />
              </button>
              <button
                onClick={() => toggleLike(album.id, "album", source)}
                className={`p-3 transition ${isLiked ? "text-yellow-500" : "text-zinc-400 hover:text-white"}`}
              >
                <Heart className="w-5 h-5" />
              </button>
              <button onClick={handleShare} className="p-3 text-zinc-400 hover:text-white transition">
                <Share2 className="w-5 h-5" />
              </button>
              {localAlbum && (
                <>
                  <button
                    onClick={() => setShowAddTracks(true)}
                    className="hidden md:flex items-center gap-2 px-4 py-2 bg-zinc-800 text-white rounded-full hover:bg-zinc-700 transition"
                  >
                    <Plus className="w-4 h-4" />
                    Add Tracks
                  </button>
                  <div className="relative">
                    <button
                      onClick={() => setShowMenu(!showMenu)}
                      className="p-3 text-zinc-400 hover:text-white transition"
                    >
                      <MoreHorizontal className="w-5 h-5" />
                    </button>

                    {showMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-zinc-800 rounded-md shadow-lg z-50">
                        <button
                          onClick={() => {
                            setShowMenu(false);
                            setShowEditDialog(true);
                          }}
                          className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-zinc-700"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit Album
                        </button>
                        <button
                          onClick={() => {
                            setShowMenu(false);
                            setShowDeleteConfirm(true);
                          }}
                          className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-zinc-700 text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Album
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Mobile Add Tracks button */}
          {localAlbum && (
            <button
              onClick={() => setShowAddTracks(true)}
              className="md:hidden w-full px-4 py-3 bg-zinc-800 text-white rounded-full hover:bg-zinc-700 transition flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Tracks
            </button>
          )}
        </div>

        {/* Track List */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <TrackList
            tracks={tracks}
            showAlbum={false}
            onPlay={(track) => {
              setCurrentTrack(track);
              setQueue(tracks);
              setIsPlaying(true);
            }}
            onRemove={localAlbum ? handleRemoveTrack : undefined}
            image={localAlbum ? album.coverUrl : spotifyAlbum?.images[0]?.url}
            title={album.name}
            subtitle={localAlbum ? album.artist : spotifyAlbum?.artists[0].name}
          />
        </motion.div>

        {/* Dialogs */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Album"
          message={`Are you sure you want to delete "${album.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          type="danger"
        />

        {showEditDialog && localAlbum && (
          <AlbumManager albumName={album.name} isOpen={true} onClose={() => setShowEditDialog(false)} />
        )}

        <AddTracksDialog
          isOpen={showAddTracks}
          onClose={() => setShowAddTracks(false)}
          onAddTracks={handleAddTracks}
          onRemoveTrack={handleRemoveTrack}
          existingTracks={tracks}
          title="Add Tracks to Album"
        />
      </div>
    </div>
  );
};
